import { Suspense, useState } from "react"
import { DelayedProgress } from "components/new-stuff/layout/RouteFallback"
import { LoadingRegion } from "components/custom/Loader"
import { ZIndex } from "constants/zIndex"
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom"
import { Button, DashboardContainer, P1, P2 } from "components"
import getIdFromKey from "utils/getIdFromKey"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { IPersonalDetails } from "./personal/interface/Personal"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { Upload } from "antd"
import CustomModal from "components/new-stuff/ui/modals/CustomModal"
import { useAppNotificationStore, useAuthStore } from "stores"
import type { RcFile, UploadFile, UploadProps } from "antd/es/upload/interface"
import type { UploadChangeParam } from "antd/es/upload"
import { InboxOutlined } from "@ant-design/icons"
import usePermissions from "hooks/usePermissions"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import ActionsButton from "components/new-stuff/ui/ActionsButton"
import useCopyToClipboard from "hooks/useCopyToClipboard"
import {
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline"
import Avatar from "components/new-stuff/ui/Avatar"
import { onQueryError } from "utils/utils"

type ActionType =
  | "delete"
  | "remove_profile"
  | "reset_password"
  // | "suspend"
  // | "reinstate"
  | "add_profile"
  | undefined

const AllRequests: React.FC<{}> = () => {
  const [showEditImageModal, setShowEditImageModal] = useState(false)
  const { pathname } = useLocation()
  const { employeeKey } = useParams()
  // const employeeId = getIdFromKey(employeeKey || "")
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()
  const user = useAuthStore((state) => state.user)

  const [actionsModal, setActionsModal] = useState({
    type: undefined as ActionType,
    employeeId: "",
    firstName: "",
  })

  const [canDeleteEmployees, canUpdateEmployees] = usePermissions({
    allowedPermissions: ["can_delete_employees", "can_update_employees"],
  })

  const isPrimaryAdmin =
    user?.isPrimaryAdmin === true || user?.userRoles?.includes("Primary Admin")

  const { isLoading: isDetailsLoading, data: employeeRes } = useQuery<
    IBaseRes<IPersonalDetails>
  >({
    queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_PERSONAL_INFO, employeeKey],
    queryFn: () => adminPeopleService.getPersonalInfo({ id: employeeKey }),
  })

  const employee = employeeRes?.data

  const tabs = [
    {
      index: 0,
      title: "Job & Pay",
      to: `/dashboard/employee/${employeeKey}/job-and-pay`,
    },
    {
      index: 1,
      title: "Personal",
      to: `/dashboard/employee/${employeeKey}/personal`,
    },
    {
      index: 2,
      title: "Request",
      to: `/dashboard/employee/${employeeKey}/requests`,
    },
    {
      index: 3,
      title: "Documents",
      to: `/dashboard/employee/${employeeKey}/documents`,
    },
    {
      index: 4,
      title: "Benefits",
      to: `/dashboard/employee/${employeeKey}/benefits`,
    },
    {
      index: 5,
      title: "Assets",
      to: `/dashboard/employee/${employeeKey}/assets`,
    },
    {
      index: 6,
      title: "Tasks",
      to: `/dashboard/employee/${employeeKey}/tasks`,
    },
    {
      index: 7,
      title: "Disciplinary",
      to: `/dashboard/employee/${employeeKey}/disciplinary`,
    },
    {
      index: 8,
      title: "Notes",
      to: `/dashboard/employee/${employeeKey}/notes`,
    },
    {
      index: 9,
      title: "Logs",
      to: `/dashboard/employee/${employeeKey}/logs`,
    },
  ]

  const actions = []

  return (
    <>
      <div className="h-full flex items-center gap-6 px-4 md:px-6 lg:px-10 border-y border-y-neutral40 bg-white overflow-x-auto">
        {tabs.map((item, index) => {
          const isActive = pathname.includes(item.to)
          return (
            <div
              className={` h-full relative flex items-center cursor-pointer py-4`}
              onClick={() => {
                navigate(item.to)
              }}
              key={index}
            >
              <P1
                className={`whitespace-nowrap leading-none ${
                  isActive ? "font-semibold text-primary500" : "text-neutral100"
                }`}
              >
                {item.title}
              </P1>
              {isActive && (
                <div className="w-full max absolute  bottom-0 h-[3px] bg-primary500" />
              )}
            </div>
          )
        })}
      </div>
      <div className="w-full mx-auto flex flex-col gap-4">
        <div className="w-full bg-[#ededed]min-h-screen flex flex-col gap-10 relative">
          <DashboardContainer>
            {isDetailsLoading ? (
              <LoadingRegion>
                <div className="w-full h-screen bg-white grid place-content-center"></div>
              </LoadingRegion>
            ) : (
              <>
                <div className="rounded-xl shadow bg-white overflow-hidden mb-6">
                  <div className="w-full h-[100px] md:h-[158px]">
                    <img
                      className="w-full h-full object-cover"
                      src={
                        "https://padefiles.blob.core.windows.net/images/86794827-c3cc-4674-81de-de3e807e6b86.png"
                      }
                      alt="logo"
                    />
                  </div>
                  <div className="py-7 px-5 flex items-start justify-between">
                    <div className=" relative w-full">
                      <div className="rounded-full overflow-hidden p-1 md:p-2 bg-white absolute -top-16 md:-top-[120px] ">
                        <div
                          className="relative group cursor-pointer h-20 w-20 md:h-32 md:w-32 overflow-hidden"
                          role="button"
                          onClick={() => {
                            if (!canUpdateEmployees) {
                              toast.unauthorized()
                              return
                            }
                            setShowEditImageModal(true)
                          }}
                        >
                          <Avatar
                            src={employee?.avatar_url}
                            alt=""
                            className="object-cover w-full h-full rounded-full"
                          />
                          <div className="hidden group-hover:grid place-content-center absolute inset-0 bg-black/60 text-white font-semibold font-circular pointer-events-none rounded-full">
                            edit
                          </div>
                        </div>
                      </div>

                      <div className=" w-full flex justify-between flex-wrap gap-4 mt-6 items-end">
                        <div>
                          <h2 className="text-[#243757] font-circular text-[28px] font-bold leading-none tracking-tight">
                            {employee?.full_name}
                          </h2>
                          <div className="flex gap-4 items-center justify-start mt-2 text-sm font-circular text-[#314158]">
                            <p>{employee?.employee_title}</p>
                            <img
                              src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/86d80373-9ee1-488a-b04a-6219e6b123a4.svg"
                              className="w-1 h-1"
                            />
                            <p className="mp-mask">
                              <a href={`tel:${employee?.mobile_phone}`}>
                                {employee?.mobile_phone}
                              </a>
                            </p>
                            <img
                              src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/86d80373-9ee1-488a-b04a-6219e6b123a4.svg"
                              className="w-1 h-1"
                            />
                            <p>{employee?.employment_status}</p>
                          </div>
                          <div className="flex items-center gap-2 justify-start mt-3">
                            <span
                              className={`font-avenir text-xs font-medium py-1.5 px-3 rounded-full ${
                                employee?.person_status === "Active"
                                  ? "bg-success100 text-success800"
                                  : "bg-danger100 text-danger500"
                              }`}
                            >
                              {employee?.person_status}
                            </span>
                            {user?.isRetroPaymentEnabled === true &&
                              employee?.is_confirmed && (
                                <span className="font-avenir text-xs font-medium py-1.5 px-3 rounded-full bg-[#f1f5f9] text-[#0f172b]">
                                  Confirmed
                                </span>
                              )}
                            {employee?.office_display_name && (
                              <span className="font-avenir text-xs font-medium py-1.5 px-3 rounded-full bg-[#ccdbfd] text-[#003bc4]">
                                {employee?.office_display_name}
                              </span>
                            )}
                          </div>
                        </div>

                        <ActionsButton
                          title="Manage"
                          items={[
                            canDeleteEmployees
                              ? {
                                  key: "2",
                                  label: (
                                    <button
                                      onClick={() => {
                                        setActionsModal({
                                          type: employee?.has_profile
                                            ? "remove_profile"
                                            : "add_profile",
                                          employeeId: employeeKey || "",
                                          firstName: employee?.first_name || "",
                                        })
                                      }}
                                      className="font-avenir w-full text-left"
                                    >
                                      {employee?.has_profile
                                        ? "Remove"
                                        : "Create"}{" "}
                                      Profile
                                    </button>
                                  ),
                                }
                              : null,
                            isPrimaryAdmin
                              ? {
                                  key: "3",
                                  label: (
                                    <button
                                      onClick={() => {
                                        setActionsModal({
                                          type: "reset_password",
                                          employeeId: employeeKey || "",
                                          firstName: employee?.first_name || "",
                                        })
                                      }}
                                      className="font-avenir w-full text-left"
                                    >
                                      Reset Password
                                    </button>
                                  ),
                                }
                              : null,
                            canDeleteEmployees
                              ? {
                                  key: "1",
                                  label: (
                                    <button
                                      onClick={() => {
                                        setActionsModal({
                                          type: "delete",
                                          employeeId: employeeKey || "",
                                          firstName: employee?.first_name || "",
                                        })
                                      }}
                                      className="font-avenir w-full text-left"
                                    >
                                      Delete
                                    </button>
                                  ),
                                }
                              : null,
                            // canDeleteEmployees
                            //   ? {
                            //       key: "4",
                            //       label: (
                            //         <button
                            //            onClick={() => {
                            //   setActionsModal({
                            //     type:
                            //       employee?.person_status_id === 3
                            //         ? "reinstate"
                            //         : "suspend",
                            //     employeeId: employeeKey || "",
                            //     firstName: employee?.first_name || "",
                            //   })
                            // }}
                            //           className="font-avenir w-full text-left"
                            //         >
                            //           {employee?.person_status_id === 3
                            // ? "Reinstate"
                            // : "Suspend"}
                            //         </button>
                            //       ),
                            //     }
                            //   : null,
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <Suspense
                  fallback={
                    <div
                      className="fixed right-0 top-32 bottom-0 left-0 md:left-[var(--sidenav-w)] transition-[left] duration-300 ease-in-out motion-reduce:transition-none flex justify-center items-center h-[calc(100vh-80px)] bg-transparent"
                      style={{
                        zIndex: ZIndex.LoadingOverlay,
                      }}
                    >
                      <DelayedProgress />
                    </div>
                  }
                >
                  <Outlet />
                </Suspense>
              </>
            )}
          </DashboardContainer>
        </div>
      </div>

      <EditUserProfileImage
        setShowEditModal={setShowEditImageModal}
        showEditModal={showEditImageModal}
        avatarUrl={employee?.avatar_url}
      />

      <DeleteRemoveSuspedModal
        type={actionsModal.type}
        employeeId={actionsModal.employeeId}
        open={
          actionsModal.type === "add_profile" ||
          actionsModal.type === "delete" ||
          actionsModal.type === "remove_profile"
        }
        onClose={() =>
          setActionsModal({
            type: undefined,
            employeeId: "",
            firstName: "",
          })
        }
        employeeFName={actionsModal?.firstName}
      />

      {actionsModal?.type === "reset_password" && (
        <ResetEmployeePasswordModal
          onClose={() =>
            setActionsModal({
              type: undefined,
              employeeId: "",
              firstName: "",
            })
          }
          fName={actionsModal?.firstName}
          emails={[employee?.email] as string[]}
          type="single-reset"
        />
      )}
    </>
  )
}

export default AllRequests

const DeleteRemoveSuspedModal = ({
  type,
  employeeId,
  employeeFName,
  open,
  onClose,
}: {
  type: ActionType
  employeeId: string
  employeeFName: string
  onClose: () => void
  open: boolean
}) => {
  const { toast } = useAppNotificationStore()
  const [showForceDeleteModal, setShowForceDeleteModal] = useState(false)
  const [warningMsg, setWarningMsg] = useState("")
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { isLoading: isDeleting, mutate: deleteEmployee } = useMutation({
    mutationFn: async () => await adminPeopleService.deleteEmployee(employeeId),
    onSuccess: (data) => {
      onClose()
      navigate("/dashboard/employees")
      toast.success(data.message)

      queryClient.invalidateQueries({
        queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_PERSONAL_INFO],
      })
    },
    onError: (e: any) => {
      const errorRes = e?.response?.data
      console.log({ errorRes })

      if (errorRes?.status?.toLowerCase() === "warning") {
        setWarningMsg(errorRes.message)
        setShowForceDeleteModal(true)
        return
      }

      onClose()
      onQueryError(e)
    },
  })

  const { isLoading: isForceDeleting, mutate: forceDeleteEmployee } =
    useMutation({
      mutationFn: async () =>
        await adminPeopleService.deleteEmployee(employeeId, true),
      onSuccess: (data) => {
        onClose()
        setShowForceDeleteModal(false)
        toast.success(data.message)
        navigate("/dashboard/employees")

        queryClient.invalidateQueries({
          queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_PERSONAL_INFO],
        })
      },
      onError: (e) => {
        onClose()
        onQueryError(e)
      },
    })

  const { isLoading: isAddingProfile, mutate: addProfile } = useMutation({
    mutationFn: async () => await adminPeopleService.addProfile(employeeId),
    onSuccess: (data) => {
      onClose()
      toast.success(data.message)

      queryClient.invalidateQueries({
        queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_PERSONAL_INFO],
      })
    },
    onError: (e) => {
      onClose()
      onQueryError(e)
    },
  })

  const { isLoading: isRemovingProfile, mutate: removeProfile } = useMutation({
    mutationFn: async () => await adminPeopleService.removeProfile(employeeId),
    onSuccess: (data) => {
      onClose()
      toast.success(data.message)

      queryClient.invalidateQueries({
        queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_PERSONAL_INFO],
      })
    },
    onError: (e) => {
      onClose()
      onQueryError(e)
    },
  })

  return (
    <>
      <CustomModal
        destroyOnClose
        open={open}
        title={
          type === "delete"
            ? `Delete Employee`
            : type === "remove_profile"
            ? `Remove Employee Profile`
            : `Create Employee Profile`
        }
        onCancel={onClose}
        width={500}
        footer={
          <div className="w-full justify-end flex gap-4">
            <Button
              title={`Cancel`}
              color="neutral"
              className="font-circular"
              key="cancel-delete"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              color={type === "add_profile" ? "primary" : "destructive"}
              type="submit"
              disabled={isDeleting || isAddingProfile || isRemovingProfile}
              submitting={isDeleting || isAddingProfile || isRemovingProfile}
              onClick={() => {
                if (type === "delete") {
                  deleteEmployee()
                  return
                }

                if (type === "add_profile") {
                  addProfile()
                  return
                }

                removeProfile()
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4 ">
          <P2 className="text-sm pb-5 text-dark30 text-[12px] leading-[18px]">
            {type === "delete"
              ? `Deleting an employee can’t be undone and we won’t keep this employee information once it has been deleted. Click Confirm to continue.`
              : type === "remove_profile"
              ? `If you remove ${employeeFName} profile, they won’t be able to login and view their payslips, make requests and participate in company activities but you’ll still be able to manage their information as normal. You can create their profile again later. Click Confirm to continue.`
              : type === "add_profile"
              ? `${employeeFName} will be able to login and view their payslips, make requests and participate in company activities after you create their user profile. You can remove their employee profile later. Click Confirm to continue.`
              : // : type === "suspend"
                // ? `If you suspend ${employeeFName}, they won’t be able to log in to view their payslips, submit requests, or participate in company activities. However, you will still retain the ability to manage their information as usual. They will also be excluded from upcoming payroll. You can reinstate them later. Please provide a reason to continue.`
                `If you reinstate ${employeeFName}, they will be able to login and view their payslips, make requests and participate in company activities. They will also be included in upcoming payroll. Click Confirm to continue.`}
          </P2>

          {/* {type === "suspend" && (
            <Input.TextArea
              className="h-10"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )} */}
        </div>
      </CustomModal>

      <CustomModal
        destroyOnClose
        open={showForceDeleteModal}
        title={`Force Delete Employee`}
        onCancel={() => {
          setShowForceDeleteModal(false)
          onClose()
        }}
        width={500}
        footer={
          <div className="w-full justify-end flex gap-4">
            <Button
              color="neutral"
              onClick={() => {
                setShowForceDeleteModal(false)
                onClose()
              }}
            >
              Cancel
            </Button>
            <Button
              color={"danger"}
              type="submit"
              disabled={isForceDeleting}
              submitting={isForceDeleting}
              onClick={() => {
                forceDeleteEmployee()
              }}
            >
              Confirm
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4 ">
          <P2 className="text-sm pb-5 text-dark30 text-[12px] leading-[18px]">
            {warningMsg}
          </P2>
        </div>
      </CustomModal>
    </>
  )
}

export const ResetEmployeePasswordModal = ({
  emails,
  fName,
  type,
  onClose,
  onSuccess,
}: {
  type: "single-reset" | "multi-reset"
  emails: string[]
  fName?: string
  onClose: () => void
  onSuccess?: () => void
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const [_, copy] = useCopyToClipboard()
  const platformName = useCustomizationSettingsStore(
    (state) => state.customizationSettings?.brand?.platformName
  )
  const { toast } = useAppNotificationStore()

  const { isLoading, mutate, data } = useMutation({
    mutationFn: async (data: string[]) => {
      return await adminPeopleService.resetPassword(data)
    },
    onSuccess: (data: any) => {
      data?.message && toast.success(data.message)
      onSuccess?.()
    },
    onError: onQueryError,
  })

  const isSuccess = data?.success
  const isMulti = type === "multi-reset"

  return (
    <>
      <CustomModal
        open
        title={
          isMulti
            ? "Reset Multiple Employees' Passwords"
            : `Reset ${fName}'s Password`
        }
        onCancel={() => {
          onClose()
        }}
        footer={
          <div className="w-full justify-end flex gap-4">
            <Button
              color="neutral"
              onClick={() => {
                onClose()
              }}
            >
              {isSuccess ? "Done" : "Cancel"}
            </Button>
            {isSuccess ? (
              <Button
                color={"primary"}
                onClick={() => {
                  copy(data?.data as string)
                  toast.success("Copied to clipboard")
                }}
              >
                Copy Password
              </Button>
            ) : (
              <Button
                color={"primary"}
                disabled={isLoading}
                submitting={isLoading}
                onClick={() => {
                  mutate(emails)
                }}
              >
                Reset
              </Button>
            )}
          </div>
        }
      >
        {isSuccess ? (
          <div className="flex flex-col gap-4">
            <P2 className="text-sm text-padeSubtext">
              Temporary password created successfully.
            </P2>

            <div className="flex gap-2 items-center">
              <P2
                className={"text-xl text-padeBlack font-semibold font-avenir"}
              >
                {showPassword ? data?.data : "********************"}
              </P2>
              <button
                onClick={() => {
                  setShowPassword(!showPassword)
                }}
                className="text-padeBlack rounded-full"
              >
                {!showPassword ? (
                  <EyeIcon className="w-5 h-5 -mt-2" />
                ) : (
                  <EyeSlashIcon className="w-5 h-5" />
                )}
              </button>
            </div>

            <div className="flex gap-2 bg-[#FFF3D8] p-4 rounded-lg ">
              <ExclamationCircleIcon className=" w-5 h-5 shrink-0 text-[#9C4C00]" />
              <div className="flex flex-col">
                <P2 className="text-sm text-padeBlack font-circular font-semibold">
                  Security Responsibility Notice
                </P2>
                <P2 className="text-sm text-padeSubtext">
                  Passwords are shared by the Admin. {platformName} is not
                  liable for any leaks or unauthorised access. Please share
                  securely and ensure employees reset their password on first
                  login.
                </P2>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 ">
            <P2 className="text-sm text-padeSubtext">
              This will generate a temporary password that{" "}
              {isMulti ? `all selected employees` : `${fName}`} must use to log
              in. They will be prompted to set a new password on their first
              login.
            </P2>
          </div>
        )}
      </CustomModal>
    </>
  )
}

const EditUserProfileImage = ({
  showEditModal,
  setShowEditModal,
  avatarUrl,
}: {
  showEditModal: boolean
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>
  avatarUrl: string | undefined
}) => {
  const { toast } = useAppNotificationStore()
  const [file, setFile] = useState<any>(null)
  const [imgUrl, setImgUrl] = useState(avatarUrl || "")
  const { employeeKey } = useParams()
  const employeeId = getIdFromKey(employeeKey || "")

  const { isLoading: isAddingFile, mutate: submitFile } = useMutation({
    mutationFn: async (formData: FormData) => {
      return await adminPeopleService.uploadImage(formData, { id: employeeId })
    },
    onSuccess: (data) => {
      toast.success(data?.message)
      setShowEditModal(false)
      setFile(null)
      setImgUrl("")
      window.location.reload()
    },
    onError: onQueryError,
  })

  const handleUpload = (id: string) => {
    if (file) {
      const formData = new FormData()
      formData.append("image_upload", file)
      formData.append("id", id)
      submitFile(formData)
    }
  }

  const beforeUpload = (file: RcFile) => {
    // Only png, jpg, jpeg file are allowed
    const isCorrectFormat =
      file.type === "image/png" ||
      file.type === "image/jpg" ||
      file.type === "image/jpeg"
    // 500kb limit

    const isLimit = file.size / 1024 / 1024 < 0.5 // 500kb

    if (!isCorrectFormat) {
      toast.error("You can only upload pdf, png, jpg, jpeg file!")
    }
    if (!isLimit) {
      toast.error("File must be smaller than 500kb!")
    }
    return isCorrectFormat && isLimit
  }

  const handleChange: UploadProps["onChange"] = (
    info: UploadChangeParam<UploadFile>
  ) => {
    if (info.file.status === "done") {
      getBase64(info.file.originFileObj as RcFile, (url) => {
        setFile(info.file.originFileObj)
        setImgUrl(url)
      })
    }
  }

  const customRequest = async (options: any) => {
    setTimeout(() => {
      options.onSuccess("ok")
    }, 0)
  }

  return (
    <CustomModal
      destroyOnClose
      open={showEditModal}
      title={"Upload Profile Picture"}
      onCancel={() => {
        setShowEditModal(false)
      }}
      width={500}
      footer={
        <div className="w-full justify-end flex gap-4">
          <Button
            title={`Cancel`}
            color="neutral"
            className="font-circular"
            key="cancel-delete"
            onClick={() => {
              setShowEditModal(false)
              setFile(null)
              setImgUrl("")
            }}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={isAddingFile}
            submitting={isAddingFile}
            onClick={() => {
              handleUpload(employeeId)
            }}
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 ">
        <P2 className="text-sm pb-5 text-dark30 text-[12px] leading-[18px]">
          We recommend a size of 200px * 200px for profile picture. Note:
          Maximum allowed file size is 500KB.
        </P2>
        <Upload.Dragger
          name="upload-companyLogo"
          className=" font-avenir"
          beforeUpload={beforeUpload}
          onChange={handleChange}
          customRequest={customRequest}
          multiple={false}
          maxCount={1}
          onDrop={(e) => {
            e.preventDefault()
          }}
          showUploadList={false}
        >
          {imgUrl ? (
            <img
              src={imgUrl}
              alt="avatar"
              className={`w-full h-full object-cover max-h-[200px]`}
            />
          ) : (
            <>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>

              <Button
                color="secondary"
                onClick={(e) => {
                  e.preventDefault()
                }}
              >
                Choose file
              </Button>
            </>
          )}
        </Upload.Dragger>
      </div>
    </CustomModal>
  )
}

export const getBase64 = async (
  img: RcFile,
  callback: (url: string) => void
) => {
  const reader = new FileReader()
  reader.addEventListener("load", () => callback(reader.result as string))
  reader.readAsDataURL(img)
}
